import { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import { NavLink } from 'react-router-dom'
import { Image as ImageIcon } from 'lucide-react'

import { arrayRemove, db } from '../../../utils/firebase'

// UI components
import Table from '../../../components/structure/table/table'
import QuoteRow from '../../../components/client/quotes/quote'
import Badge from '../../../components/ui/badge/badge'
import Button from '../../../components/ui/button/button'
import { CreateQuoteModal } from '../../enquiry/_components/new-quote/new-quote'

import './quotes.scss'

const toMillis = (timestamp) => {
    if (!timestamp) return 0
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis()
    if (timestamp.seconds || timestamp._seconds) return (timestamp.seconds || timestamp._seconds) * 1000
    return 0
}

// Map a durable public-quote projection (public_quotes/{token}) into the same
// shape the builder-quote cards read, so a sent quote whose enquiry has been
// deleted on conversion still renders. `_orphan` tells the card the builder +
// enquiry are gone, so it links out to the live /quote/{token} page rather than
// opening the in-app builder.
const publicQuoteToRow = (pq) => {
    const hotelNames = Array.isArray(pq.hotel_names) ? pq.hotel_names : []
    const rounds = Number(pq.rounds) || 0
    return {
        id: pq.quote_id,
        enquiryID: pq.enquiry_id,
        reference: pq.reference || '',
        nights: Number(pq.nights) || 0,
        group_size: Number(pq.group_size) || 0,
        travel_millis: Number(pq.travel_millis) || 0,
        // Minimal basket so the cards derive the heading, hero image + round count.
        basket: [
            ...hotelNames.map((title, index) => ({ type: 'hotel', title, image: index === 0 ? pq.hero_image : '' })),
            ...Array.from({ length: rounds }).map(() => ({ type: 'round', title: 'Golf' })),
        ],
        sell_split: Boolean(pq.sell_split),
        sell_lines: Array.isArray(pq.sell_lines) ? pq.sell_lines : [],
        sell_at: Number(pq.sell_per_person) || 0,
        agent: pq.agent_id || '',
        is_active: pq.status !== 'cancelled',
        is_cancelled: pq.status === 'cancelled',
        sent_at: pq.updated || pq.created || null,
        created: pq.created || null,
        updated: pq.updated || null,
        public_token: pq.token,
        public_status: pq.status,
        booking_id: pq.booking_id || null,
        _orphan: true,
    }
}

// Returns the markup for the clients quotes
export default function Quotes({ clientID }) {
    const [quotes, setQuotes] = useState([])
    const [deleting, setDeleting] = useState(null)

    // Builder-made quotes (enquiries/{id}/quotes) across the client's enquiries.
    const [enquiryQuotes, setEnquiryQuotes] = useState([])
    const [loadingEnquiryQuotes, setLoadingEnquiryQuotes] = useState(true)
    // Agent names for the Sent/Saved by-lines, keyed by user ID.
    const [agentsByID, setAgentsByID] = useState({})
    // The builder quote open in the viewer, or null. Cancelled quotes open
    // read-only; drafts/sent quotes open editable, exactly like the enquiry page.
    const [openQuote, setOpenQuote] = useState(null)

    // On component load
    useEffect(() => {
        // Fetch the quotes belonging to this client
        fetchClientQuotes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Fetch the quotes matching this clients Id
    const fetchClientQuotes = async () => {
        // Find all the quotes attached to this user
        db.collection('quotes')
            .orderBy('created', 'desc')
            .where('client', '==', clientID)
            .get().then((quoteDocs) => {
                let quotesArr = [];

                // Loop over each of the quote documents
                quoteDocs.forEach((enquiry) => {
                    // Push the quote details onto the array
                    quotesArr.push({
                        id: enquiry.id,
                        ...enquiry.data(),
                    })
                })

                // Then set these into the state
                setQuotes(quotesArr)
            })
    }

    // Builder quotes live under enquiries/{id}/quotes, so walk the client's
    // enquiries array and pull each enquiry's quotes (drafts, sent + cancelled).
    // Then merge in the durable public projection (public_quotes), which is the
    // only place a sent quote survives once its enquiry is deleted on conversion -
    // so converted-enquiry quotes (and their live links) come back onto the page.
    const fetchEnquiryQuotes = useCallback(async () => {
        setLoadingEnquiryQuotes(true)
        try {
            const clientDoc = await db.doc(`clients/${clientID}`).get()
            const enquiryIDs = clientDoc.data()?.enquiries || []

            const internalRows = []
            await Promise.all(
                enquiryIDs.map(async (enquiryID) => {
                    try {
                        const quoteDocs = await db.collection(`enquiries/${enquiryID}/quotes`).get()
                        quoteDocs.forEach((quoteDoc) => internalRows.push({ id: quoteDoc.id, enquiryID, ...quoteDoc.data() }))
                    } catch (err) {
                        // Non-fatal - one unreadable enquiry shouldn't hide the rest.
                    }
                })
            )

            // Sent quotes from the durable, client-stamped public projection.
            const publicRows = []
            try {
                const publicDocs = await db.collection('public_quotes').where('client', '==', clientID).get()
                publicDocs.forEach((doc) => publicRows.push({ token: doc.id, ...doc.data() }))
            } catch (err) {
                // Non-fatal - fall back to just the builder quotes.
            }

            // Attach each sent quote's public token to its still-live builder quote
            // (so it gains a "Client link"); synthesise cards for sent quotes whose
            // enquiry is gone - the converted ones a plain walk can no longer find.
            const internalByQuoteID = new Map(internalRows.map((row) => [row.id, row]))
            publicRows.forEach((pq) => {
                const match = internalByQuoteID.get(pq.quote_id)
                if (match) {
                    match.public_token = pq.token
                    match.public_status = pq.status
                    match.booking_id = pq.booking_id || match.booking_id || null
                }
            })
            const orphanRows = publicRows
                .filter((pq) => pq.quote_id && !internalByQuoteID.has(pq.quote_id))
                .map(publicQuoteToRow)

            const rows = [...internalRows, ...orphanRows]
            rows.sort((a, b) => toMillis(b.sent_at || b.updated || b.created) - toMillis(a.sent_at || a.updated || a.created))

            // Resolve the sending/saving agents for the cards' by-lines.
            const agentIDs = Array.from(new Set(rows.map((row) => row.agent).filter(Boolean)))
            const agentDocs = await Promise.all(
                agentIDs.map((agentID) => db.doc(`users/${agentID}`).get().then((doc) => [agentID, doc.data()]))
            )
            const nextAgents = {}
            agentDocs.forEach(([agentID, agent]) => {
                if (agent) nextAgents[agentID] = `${agent.first_name || ''} ${agent.last_name || ''}`.trim()
            })

            setAgentsByID(nextAgents)
            setEnquiryQuotes(rows)
        } catch (err) {
            console.error('Error loading enquiry quotes:', err)
            setEnquiryQuotes([])
        } finally {
            setLoadingEnquiryQuotes(false)
        }
    }, [clientID])

    useEffect(() => {
        fetchEnquiryQuotes()
    }, [fetchEnquiryQuotes])

    // Delete a quote from the database
    const deleteQuote = async (quoteID) => {
        // Set the ID into the state
        setDeleting(quoteID)

        // First remove the quote ID from the clients array
        await db.doc(`clients/${clientID}`).set({
            quotes: arrayRemove(quoteID)
        }, { merge: true })

        // And then delete the quote document itself
        await db.doc(`quotes/${quoteID}`).delete()

        // Re-fetch the quotes
        fetchClientQuotes()

        // Reset the state
        setDeleting(null)
    }

    return (
        <>
            {/* Builder-made quotes as mini cards, above the legacy quote table.
                Live quotes (drafts + sent) get the full card; cancelled/closed
                ones collapse to slim image-less rows so they don't eat the page. */}
            {(loadingEnquiryQuotes || enquiryQuotes.length > 0) && (
                <div className="client-quote-cards-section">
                    <h2 className="client-quote-cards-title">Quotes ({enquiryQuotes.length})</h2>
                    {loadingEnquiryQuotes ? (
                        <small className="no-value">Loading quotes...</small>
                    ) : (
                        (() => {
                            const isClosed = (quote) => Boolean(quote.is_cancelled) || (!quote.is_draft && !quote.is_active)
                            const liveQuotes = enquiryQuotes.filter((quote) => !isClosed(quote))
                            const closedQuotes = enquiryQuotes.filter(isClosed)
                            return (
                                <>
                                    {liveQuotes.length > 0 && (
                                        <div className="client-quote-cards">
                                            {liveQuotes.map((quote) => (
                                                <MiniQuoteCard
                                                    key={`${quote.enquiryID}-${quote.id}`}
                                                    quote={quote}
                                                    agentName={agentsByID[quote.agent] || ''}
                                                    onOpen={() => setOpenQuote(quote)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {closedQuotes.length > 0 && (
                                        <div className="client-quote-rows">
                                            {closedQuotes.map((quote) => (
                                                <CompactQuoteRow
                                                    key={`${quote.enquiryID}-${quote.id}`}
                                                    quote={quote}
                                                    onOpen={() => setOpenQuote(quote)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )
                        })()
                    )}
                </div>
            )}

            <Table
                className="enquiries-table"
                headings={[
                    "Created",
                    "Agent",
                    "Reference(s)"
                ]}
                noResults={quotes.length === 0}
                noResultsMessage="This client hasn't got any quotes">

                {/* Print out the quotes for this user */}
                {quotes.map((quote) => (
                    <QuoteRow
                        key={quote.id}
                        id={quote.id}
                        details={quote}
                        deleting={deleting === quote.id}
                        deleteQuote={(quoteID) => deleteQuote(quoteID)} />
                ))}
            </Table>

            {/* Open a builder quote in the full builder modal (read-only when the
                quote has been cancelled, matching the enquiry page). */}
            {openQuote && (
                <CreateQuoteModal
                    seed={null}
                    enquiryID={openQuote.enquiryID}
                    draftId={openQuote.id}
                    initialDraft={openQuote}
                    readOnly={Boolean(openQuote.is_cancelled)}
                    onClose={() => setOpenQuote(null)}
                    onSaved={fetchEnquiryQuotes}
                />
            )}
        </>
    );
}

// Small fallback-aware thumbnail for the mini cards (only absolute http(s)
// URLs render; anything else shows the placeholder tile).
function MiniQuoteImage({ src }) {
    const [failed, setFailed] = useState(false)
    const usable = typeof src === 'string' && /^https?:\/\//i.test(src)

    if (!usable || failed) {
        return (
            <div className="client-quote-card-thumb is-placeholder">
                <ImageIcon size={20} />
            </div>
        )
    }

    return <img className="client-quote-card-thumb" src={src} alt="" onError={() => setFailed(true)} />
}

/**
 * Mini version of the enquiry page's quote cards: image, status + reference,
 * the hotel heading and a one-line trip summary, with the sell price on the
 * right and View enquiry / View quote actions.
 */
function MiniQuoteCard({ quote, agentName, onOpen }) {
    const basket = Array.isArray(quote.basket) ? quote.basket : []
    const hotels = basket.filter((item) => item.type === 'hotel')
    const rounds = basket.filter((item) => item.type === 'round')

    const hotelNames = [...new Set(hotels.map((item) => item.title).filter(Boolean))]
    const heading = hotelNames.length ? hotelNames.join(', ') : rounds[0]?.title || basket[0]?.title || 'Quote'
    const image = hotels.find((item) => item.image)?.image || rounds.find((item) => item.image)?.image || ''

    const nights = Number(quote.nights) || 0
    const guests = Number(quote.group_size) || 0
    const trip = [
        quote.travel_millis ? moment(quote.travel_millis).format('D MMM YYYY') : null,
        nights ? `${nights} night${nights === 1 ? '' : 's'}` : null,
        guests ? `${guests} guest${guests === 1 ? '' : 's'}` : null,
        rounds.length ? `${rounds.length} round${rounds.length === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' · ')

    // Sell price(s): split quotes list each per-person tier, otherwise the
    // single denormalised figure - mirroring the enquiry page cards.
    const sellLines = quote.sell_split && Array.isArray(quote.sell_lines) ? quote.sell_lines : []
    const splitPrices = sellLines.map((line) => Number(line.price) || 0).filter((price) => price > 0)
    const sellPrices = splitPrices.length ? splitPrices : quote.sell_at ? [Number(quote.sell_at) || 0] : []

    const booked = quote.public_status === 'booked' || Boolean(quote.booking_id)
    const status = booked
        ? { label: 'Booked', type: 'SPECIAL' }
        : quote.is_active
            ? { label: 'Sent', type: 'POSITIVE' }
            : { label: 'Draft', type: 'AMBER' }

    // "Sent 11 Jun 2026 at 17:43 by Lewis Newson" (Saved for drafts).
    const savedMillis = toMillis(quote.sent_at || quote.updated || quote.created)
    const savedLine = savedMillis > 0
        ? `${quote.is_active ? 'Sent' : 'Saved'} ${moment(savedMillis).format('D MMM YYYY [at] HH:mm')}${agentName ? ` by ${agentName}` : ''}`
        : ''

    return (
        <div className="client-quote-card">
            <MiniQuoteImage src={image} />

            <div className="client-quote-card-body">
                <div className="client-quote-card-badges">
                    <Badge label={status.label} type={status.type} />
                    {quote.reference && <span className="client-quote-card-reference">{quote.reference}</span>}
                </div>
                <p className="client-quote-card-heading">{heading}</p>
                {trip && <p className="client-quote-card-trip">{trip}</p>}
                {savedLine && <p className="client-quote-card-saved">{savedLine}</p>}
            </div>

            <div className="client-quote-card-price">
                {sellPrices.length > 0 ? (
                    <span className="client-quote-card-sell">
                        {sellPrices.map((price, index) => (
                            <span key={index}>
                                {index > 0 && <span className="client-quote-card-sell-sep">/</span>}
                                £{Math.round(price).toLocaleString()}<small>pp</small>
                            </span>
                        ))}
                    </span>
                ) : (
                    <span className="client-quote-card-sell is-muted">Not priced</span>
                )}
            </div>

            <div className="client-quote-card-actions">
                {quote._orphan ? (
                    // The enquiry + builder draft are gone (deleted on conversion);
                    // the live public page is all that survives, so link straight to it.
                    <a href={`/quote/${quote.public_token}`} target="_blank" rel="noopener noreferrer">
                        <Button small={true} label="View quote" />
                    </a>
                ) : (
                    <>
                        <NavLink to={`/enquiry/${quote.enquiryID}`}>
                            <Button small={true} className="OUTLINE" label="View enquiry" />
                        </NavLink>
                        {quote.public_token && (
                            <a href={`/quote/${quote.public_token}`} target="_blank" rel="noopener noreferrer">
                                <Button small={true} className="OUTLINE" label="Client link" />
                            </a>
                        )}
                        <Button small={true} label="View quote" onClick={onOpen} />
                    </>
                )}
            </div>
        </div>
    )
}

/**
 * Slim image-less row for a cancelled/closed quote, so the dead ones don't eat
 * vertical space: grey status badge, reference, heading and sell price on one
 * line, with the same View enquiry / View quote actions.
 */
function CompactQuoteRow({ quote, onOpen }) {
    const basket = Array.isArray(quote.basket) ? quote.basket : []
    const hotels = basket.filter((item) => item.type === 'hotel')
    const rounds = basket.filter((item) => item.type === 'round')

    const hotelNames = [...new Set(hotels.map((item) => item.title).filter(Boolean))]
    const heading = hotelNames.length ? hotelNames.join(', ') : rounds[0]?.title || basket[0]?.title || 'Quote'

    const sellLines = quote.sell_split && Array.isArray(quote.sell_lines) ? quote.sell_lines : []
    const splitPrices = sellLines.map((line) => Number(line.price) || 0).filter((price) => price > 0)
    const sellPrices = splitPrices.length ? splitPrices : quote.sell_at ? [Number(quote.sell_at) || 0] : []

    return (
        <div className="client-quote-row">
            <Badge label={quote.is_cancelled ? 'Cancelled' : 'Closed'} type="DEFAULT" />
            <p className="client-quote-row-heading">{heading}</p>
            {quote.reference && <span className="client-quote-row-reference">{quote.reference}</span>}

            <span className="client-quote-row-sell">
                {sellPrices.length > 0
                    ? sellPrices.map((price) => `£${Math.round(price).toLocaleString()}pp`).join(' / ')
                    : ''}
            </span>

            <div className="client-quote-row-actions">
                {quote._orphan ? (
                    <a href={`/quote/${quote.public_token}`} target="_blank" rel="noopener noreferrer">
                        <Button small={true} label="View quote" />
                    </a>
                ) : (
                    <>
                        <NavLink to={`/enquiry/${quote.enquiryID}`}>
                            <Button small={true} className="OUTLINE" label="View enquiry" />
                        </NavLink>
                        {quote.public_token && (
                            <a href={`/quote/${quote.public_token}`} target="_blank" rel="noopener noreferrer">
                                <Button small={true} className="OUTLINE" label="Client link" />
                            </a>
                        )}
                        <Button small={true} label="View quote" onClick={onOpen} />
                    </>
                )}
            </div>
        </div>
    )
}
